import classNames from 'classnames';
import type { ReactNode } from 'react';

import { Avatar } from '../Avatar';
import { Icon } from '../Icon';
import type { IconName } from '../Icon';

import styles from './NotificationMedia.module.css';

export type NotificationMediaVariant = 'success' | 'warning' | 'error' | 'info' | 'gray';
/** How a passed-in icon is framed: soft rounded square, or animated concentric ripples. */
export type NotificationIconShape = 'ripple' | 'square';

/**
 * Leading visual of a notification. `icon` frames any glyph as a rippling featured icon or a
 * bordered square; `image` is a flush-left picture; `avatar` a round user photo with an optional
 * presence dot; `none` drops the visual entirely.
 */
export type NotificationMediaSpec =
  | {
      kind: 'icon';
      icon?: ReactNode;
      variant?: NotificationMediaVariant;
      shape?: NotificationIconShape;
    }
  | { kind: 'image'; src: string; alt?: string }
  | { kind: 'avatar'; src?: string; name?: string; online?: boolean }
  | { kind: 'none' };

/** Glyph shown when an `icon` media doesn't pass its own. Mirrors `FeaturedIcon`. */
const DEFAULT_ICON: Record<NotificationMediaVariant, IconName> = {
  success: 'check-circle',
  warning: 'alert-triangle',
  error: 'alert-circle',
  info: 'info-circle',
  gray: 'alert-circle',
};

const GLYPH_SIZE = 24;
/** Three staggered rings read as a continuous water-drop ripple. */
const RIPPLE_RING_COUNT = 3;

export interface NotificationMediaProps {
  spec: NotificationMediaSpec;
  className?: string;
}

/**
 * Renders the inline leading media of a `Notification` (image is handled by the card itself so it
 * can sit flush against the edge). The `ripple` icon shape animates concentric rings outward like
 * a drop hitting water; the animation stops under `prefers-reduced-motion`.
 */
export function NotificationMedia({ spec, className }: NotificationMediaProps) {
  if (spec.kind === 'avatar') {
    return (
      <Avatar
        size="md"
        className={className}
        {...(spec.src != null ? { src: spec.src } : {})}
        {...(spec.name != null ? { name: spec.name } : {})}
        {...(spec.online === true ? { indicator: 'online' as const } : {})}
      />
    );
  }

  if (spec.kind === 'icon') {
    const variant = spec.variant ?? 'gray';
    const glyph = spec.icon ?? <Icon name={DEFAULT_ICON[variant]} size={GLYPH_SIZE} />;

    if (spec.shape === 'square') {
      return <span className={classNames(styles.square, className)}>{glyph}</span>;
    }

    return (
      <span className={classNames(styles.ripple, styles[variant], className)}>
        {Array.from({ length: RIPPLE_RING_COUNT }, (_, ring) => (
          <span key={ring} className={styles.ring} aria-hidden="true" />
        ))}
        <span className={styles.glyph}>{glyph}</span>
      </span>
    );
  }

  return null;
}
